import { Link } from "react-router-dom";
import { Flame, Droplets, Mountain, Quote } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "../components/Reveal";

const DOSHA = [
  {
    icon: Flame,
    name: "Pitta",
    elements: "Fuoco + Acqua",
    need: "Ha bisogno di morbidezza e decompressione.",
    bg: "bg-earth-mist",
    fg: "text-earth",
  },
  {
    icon: Droplets,
    name: "Vata",
    elements: "Aria + Etere",
    need: "Ha bisogno di stabilità, calore e ritmo.",
    bg: "bg-lavanda-mist",
    fg: "text-lavanda",
  },
  {
    icon: Mountain,
    name: "Kapha",
    elements: "Acqua + Terra",
    need: "Ha bisogno di movimento e attivazione.",
    bg: "bg-sage-mist",
    fg: "text-sage",
  },
];

const ChiSono = () => (
  <div data-testid="chi-sono-page" className="pt-40">
    <header className="mx-auto grid max-w-6xl items-center gap-16 px-6 lg:grid-cols-[0.9fr_1.1fr]">
      <Reveal>
        <div className="relative">
          <div className="overflow-hidden rounded-[3rem] lg:-rotate-2 transition-transform duration-700 hover:rotate-0">
            <img src="/assets/agnese-ritratto.jpg" alt="Agnese Caon" data-testid="chi-sono-portrait" className="aspect-[4/5] w-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-4 rounded-[2rem] bg-cream-soft/85 px-6 py-4 shadow-[0_12px_32px_rgba(48,51,48,0.1)] backdrop-blur-xl sm:-right-8">
            <p className="font-serif text-lg italic text-ink">Agnese Caon</p>
            <p className="text-xs uppercase tracking-[0.25em] text-sage">Operatrice del Benessere</p>
          </div>
        </div>
      </Reveal>
      <div>
        <Reveal y={16}><Eyebrow>Chi Sono</Eyebrow></Reveal>
        <h1 className="mt-6 font-serif text-5xl leading-tight sm:text-6xl" data-testid="chi-sono-title">
          <MaskedLine delay={0.1} inView>Ciao, sono</MaskedLine>
          <MaskedLine delay={0.22} inView><span className="italic text-sage">Agnese</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <div className="mt-8 space-y-5 text-base leading-relaxed text-ink/65">
            <p>
              Artemisia nasce dal mio cammino personale: anni di studio e pratica tra
              massaggio ayurvedico, yoga, meditazione e alimentazione consapevole,
              intrecciati in un unico modo di stare al mondo.
            </p>
            <p>
              Credo che il corpo sia un sistema vivo e intelligente, capace di guidarci se
              impariamo ad ascoltarlo. Per questo ogni incontro è costruito sulla persona
              che ho di fronte: non protocolli, ma presenza.
            </p>
            <p>
              Il mio invito è semplice: fare le cose con leggerezza, portando il cento per
              cento di noi stessi in ciò che facciamo.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-10 flex flex-wrap gap-3">
            {["Massaggio Olistico", "Yoga Dolce", "Meditazione", "Cucina Consapevole"].map((t) => (
              <span key={t} className="rounded-full bg-sage-mist px-5 py-2 text-xs font-medium text-sage-deep">{t}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </header>

    {/* FILOSOFIA */}
    <section className="mx-auto max-w-4xl px-6 pt-36 text-center">
      <Reveal>
        <Quote className="mx-auto h-8 w-8 text-lavanda/50" />
        <p className="mt-8 font-serif text-3xl italic leading-relaxed text-ink sm:text-4xl" data-testid="filosofia-quote">
          «Il tocco non è meccanico, il silenzio è parte della cura.»
        </p>
        <p className="mt-8 text-sm uppercase tracking-[0.3em] text-ink/40">La filosofia Artemisia</p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-10 text-base leading-relaxed text-ink/65">
          Ogni trattamento è un incontro. Prima delle tecniche c'è l'ascolto: del respiro,
          delle tensioni, di ciò che il corpo racconta senza parole. Da lì nasce un percorso
          che integra mente, corpo, respiro ed energia — perché il benessere non si riceve,
          si coltiva insieme.
        </p>
      </Reveal>
    </section>

    {/* DOSHA */}
    <section className="mx-auto max-w-6xl px-6 pt-32">
      <Reveal className="max-w-2xl">
        <Eyebrow>La Visione Ayurvedica</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl" data-testid="dosha-title">
          Ogni persona è <span className="italic text-lavanda">un equilibrio unico</span>
        </h2>
        <p className="mt-6 text-base leading-relaxed text-ink/60">
          Secondo l'Ayurveda siamo un intreccio di tre energie, i Dosha. Conoscerle significa
          sapere di cosa abbiamo davvero bisogno.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {DOSHA.map((d, i) => (
          <Reveal key={d.name} delay={0.1 * i}>
            <div data-testid={`dosha-${d.name.toLowerCase()}`} className="group h-full rounded-[2.5rem] bg-cream-soft p-9 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_56px_rgba(48,51,48,0.1)]">
              <div className={`flex h-14 w-14 items-center justify-center rounded-full ${d.bg} ${d.fg} transition-transform duration-500 group-hover:scale-110`}>
                <d.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-7 font-serif text-2xl">{d.name}</h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.25em] text-ink/40">{d.elements}</p>
              <p className="mt-4 text-sm leading-relaxed text-ink/60">{d.need}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="mx-auto max-w-6xl px-6 pt-32">
      <Reveal>
        <div className="rounded-[3rem] bg-lavanda-mist px-8 py-16 text-center sm:py-20">
          <h2 className="mx-auto max-w-xl font-serif text-3xl italic leading-snug text-ink sm:text-4xl">
            Vieni a conoscermi: il primo passo è una chiacchierata
          </h2>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-5">
            <Link to="/contatti" data-testid="chi-sono-cta" className="rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-8 py-4 text-sm font-medium text-cream transition-transform duration-500 hover:scale-105">
              Prenota un incontro
            </Link>
            <a href="tel:+393488598613" data-testid="chi-sono-phone" className="text-sm font-medium text-sage-deep underline decoration-sage/40 underline-offset-8 transition-colors hover:decoration-sage">
              348 859 8613
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  </div>
);

export default ChiSono;
